import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { MapPin, Mountain, ArrowRight } from "lucide-react";

export default function Estates() {
  const [estates, setEstates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/estates").then((r) => setEstates(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="estates-page" className="pt-32 pb-24">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 mb-20">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
          Partnered Estates
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl font-light leading-[0.95] max-w-4xl">
          Where every<br />bean has a<br /><em className="not-italic text-[#C85A32]">home.</em>
        </h1>
        <p className="mt-8 max-w-3xl text-lg text-[#4A3B32] leading-relaxed">
          We work directly with a handful of estates across the Western Ghats — long-term partnerships, transparent pricing,
          and coffee traceable to the block. Meet the places that grow our beans.
        </p>
      </section>

      {loading ? (
        <div className="py-24 text-center font-serif-display text-3xl text-[#7B6F66]">Loading…</div>
      ) : estates.length === 0 ? (
        <div className="py-24 text-center font-serif-display text-3xl text-[#7B6F66]">
          Estate stories coming soon.
        </div>
      ) : (
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 space-y-24">
          {estates.map((e, i) => (
            <article
              key={e.id}
              data-testid={`estate-${e.slug}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center ${
                i % 2 === 1 ? "md:[direction:rtl]" : ""
              }`}
            >
              <div className={`md:col-span-7 aspect-[16/10] overflow-hidden ${i % 2 === 1 ? "md:[direction:ltr]" : ""}`}>
                {e.image && (
                  <img src={e.image} alt={e.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className={`md:col-span-5 ${i % 2 === 1 ? "md:[direction:ltr]" : ""}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3 flex items-center gap-2">
                  <MapPin size={12} /> {e.region}
                </div>
                <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-none text-[#1C3326]">
                  {e.name}
                </h2>
                {e.tagline && <p className="mt-3 text-lg text-[#7B6F66]">{e.tagline}</p>}
                <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-xs uppercase tracking-[0.15em] text-[#7B6F66]">
                  {e.altitude && (
                    <span className="inline-flex items-center gap-2">
                      <Mountain size={12} /> {e.altitude}
                    </span>
                  )}
                  {e.established && <span>{e.established}</span>}
                </div>
                <p className="mt-6 text-[#4A3B32] leading-relaxed">{e.story}</p>
                {e.varieties && e.varieties.length > 0 && (
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B6F66] mb-3">Varieties</div>
                    <div className="flex flex-wrap gap-2">
                      {e.varieties.map((v) => (
                        <span key={v} className="border border-[#EBE3D5] px-3 py-1 text-xs uppercase tracking-[0.15em]">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <Link
                  to="/events"
                  className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.25em] link-underline text-[#1C3326]"
                  data-testid={`estate-events-${e.slug}`}
                >
                  Upcoming micro-lots & events <ArrowRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
